import { ChartNoAxesColumn, FolderOpen, LogOut, Settings, Users, X } from 'lucide-react'
import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import type { LucideProps } from 'lucide-react'
import type { ComponentType } from 'react'

import { useSession } from '../features/auth/useSession'
import { SupportChat } from '../features/support/SupportChat'
import { ICON_STROKE, cx } from '../ui'

/**
 * Layout del estudio, responsive de verdad.
 *
 * Escritorio: barra lateral fija.
 * Móvil:      barra inferior de navegación (alcance del pulgar) y cabecera
 *             compacta. NO un menú lateral oculto tras una hamburguesa: con
 *             3-4 secciones, la barra inferior es más rápida y descubrible.
 */

interface NavItem {
  to: string
  label: string
  icon: ComponentType<LucideProps>
}

/*
 | «Uso» pasa a «Plan y uso» y «Ajustes» a «Identidad».
 |
 | La etiqueta de una sección debería decir qué vas a encontrar, no cómo se
 | llama la tabla. «Ajustes» es el cajón donde va lo que nadie supo dónde
 | poner; lo que hay detrás es el color y el tema con los que tus clientes ven
 | tus galerías, y eso tiene nombre.
 */
const NAVIGATION: readonly NavItem[] = [
  { to: '/projects', label: 'Proyectos', icon: FolderOpen },
  { to: '/team', label: 'Equipo', icon: Users },
  { to: '/usage', label: 'Plan y uso', icon: ChartNoAxesColumn },
  { to: '/settings', label: 'Identidad', icon: Settings },
]

export function AppLayout() {
  const { tenant, logout } = useSession()
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <div className="flex min-h-dvh flex-col md:flex-row">
      {/* Barra lateral — sólo escritorio */}
      <aside className="hidden border-r border-line bg-surface-raised p-4 md:flex md:w-60 md:flex-col">
        <TenantBadge name={tenant?.name} logo={tenant?.branding.logo_url} />

        <nav className="mt-7 flex flex-col gap-0.5">
          {NAVIGATION.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-2.5 rounded-control px-3 py-2 text-label transition-colors',
                  isActive
                    ? 'bg-accent-subtle font-medium text-accent'
                    : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
                )
              }
            >
              <item.icon size={17} strokeWidth={ICON_STROKE} aria-hidden />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button
          onClick={() => void logout()}
          className="mt-auto flex items-center gap-2.5 rounded-control px-3 py-2 text-left text-label text-ink-subtle transition-colors hover:bg-surface-sunken hover:text-ink"
        >
          <LogOut size={17} strokeWidth={ICON_STROKE} aria-hidden />
          Cerrar sesión
        </button>
      </aside>

      {/* Cabecera — sólo móvil. pt respeta el notch. */}
      <header className="flex items-center justify-between border-b border-line bg-surface-raised px-4 py-3 pt-safe-3 md:hidden">
        <TenantBadge name={tenant?.name} logo={tenant?.branding.logo_url} />

        <button
          onClick={() => setMenuOpen((open) => !open)}
          aria-label={menuOpen ? 'Cerrar menú de cuenta' : 'Menú de cuenta'}
          aria-expanded={menuOpen}
          className="-mr-2 grid h-10 w-10 place-items-center rounded-control text-ink-muted"
        >
          {menuOpen ? (
            <X size={19} strokeWidth={ICON_STROKE} aria-hidden />
          ) : (
            // Tres puntos dibujados, no el carácter «⋯»: ese glifo cambia de
            // ancho y de altura de línea según la fuente del sistema.
            <span aria-hidden className="flex gap-[3px]">
              <span className="h-1 w-1 rounded-full bg-current" />
              <span className="h-1 w-1 rounded-full bg-current" />
              <span className="h-1 w-1 rounded-full bg-current" />
            </span>
          )}
        </button>
      </header>

      {menuOpen && (
        <div className="border-b border-line bg-surface-raised px-4 py-1 md:hidden">
          <button
            onClick={() => void logout()}
            className="flex w-full items-center gap-2.5 py-3 text-label text-ink-muted"
          >
            <LogOut size={17} strokeWidth={ICON_STROKE} aria-hidden />
            Cerrar sesión
          </button>
        </div>
      )}

      {/* pb en móvil para que la barra inferior no tape el contenido */}
      <main className="flex-1 px-4 py-5 pb-safe-nav md:px-8 md:py-7 md:pb-7">
        <Outlet />
      </main>

      {/* Navegación inferior — sólo móvil, dentro del alcance del pulgar */}
      <nav className="fixed inset-x-0 bottom-0 flex border-t border-line bg-surface pb-safe md:hidden">
        {NAVIGATION.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cx(
                'flex flex-1 flex-col items-center gap-1 py-2 text-micro transition-colors',
                isActive ? 'font-medium text-accent' : 'text-ink-subtle',
              )
            }
          >
            <item.icon size={20} strokeWidth={ICON_STROKE} aria-hidden />
            {item.label}
          </NavLink>
        ))}
      </nav>
      {/* El chat de ayuda acompaña en todas las pantallas del estudio: la duda
          llega subiendo una boda, no en una sección aparte. */}
      <SupportChat />
    </div>
  )
}

function TenantBadge({ name, logo }: { name?: string; logo?: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {logo !== undefined ? (
        <img src={logo} alt="" className="h-8 w-8 shrink-0 rounded-control object-contain" />
      ) : (
        // Monograma en lugar del cuadrado de color plano que había. Mientras el
        // estudio no suba su logo, su inicial es más suya que una mancha, y
        // hace que el panel no parezca a medio configurar.
        <span
          aria-hidden
          className="grid h-8 w-8 shrink-0 place-items-center rounded-control bg-accent text-label font-semibold text-accent-contrast"
        >
          {(name ?? 'r').trim().charAt(0).toUpperCase()}
        </span>
      )}
      <span className="truncate text-label font-semibold text-ink">{name ?? 'ChasquiTambo'}</span>
    </div>
  )
}
